import { Button } from "@indigo-components";
import { useSetAtom } from "jotai";
import { type ErrorInfo, useEffect } from "react";
import { FallbackProps } from "react-error-boundary";
import { outOfSyncAtom } from "~/atoms/outOfSync";
import { getCurrentEnv } from "~/common/utils";
import {
  OUT_OF_SYNC_MESSAGE,
  OUT_OF_SYNC_TITLE,
} from "~/components/outOfSyncCopy";
import { detectAppointmentIdFromURL } from "./correlationContext";
import { errorReporter } from "./errorReporter";
import { isChunkLoadError } from "./errorUtils";

export function DefaultFallbackComponent({
  error,
  resetErrorBoundary,
}: FallbackProps) {
  const chunkLoadError = isChunkLoadError(error);
  const setOutOfSync = useSetAtom(outOfSyncAtom);
  // chunkLoadRecovery flags the modal for every chunk failure, but once a
  // boundary has replaced the page with this Reload screen the modal is a
  // duplicate of it — dismiss it.
  useEffect(() => {
    if (chunkLoadError) setOutOfSync(false);
  }, [chunkLoadError, setOutOfSync]);

  // A failed lazy import means this tab is running an older deploy whose chunks
  // no longer exist at origin. "Try again" would just re-fetch the same missing
  // chunk — only a reload recovers. (The page is already torn down here, so
  // unlike the in-page OutOfSyncModal there's no unsaved work left to protect.)
  if (chunkLoadError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-4 mt-10 text-indigo-text-primary">
        <h2 className="text-h2">{OUT_OF_SYNC_TITLE}</h2>
        <p className="max-w-md text-center text-indigo-text-secondary">
          {OUT_OF_SYNC_MESSAGE}
        </p>
        <Button
          onClick={() => window.location.reload()}
          size="md"
          variant="primary"
        >
          Reload
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-4 mt-10 text-indigo-text-primary">
      <h2 className="text-h2">Something went wrong</h2>
      <p className="text-indigo-text-secondary">
        We&apos;ve been notified and are working to fix this issue.
      </p>
      <Button onClick={resetErrorBoundary} size="md" variant="primary">
        Try again
      </Button>
      {getCurrentEnv() !== "prod" && (
        <pre className="font-mono mt-10 max-w-full overflow-x-auto rounded-indigo-md bg-indigo-canvas-shade p-4 text-indigo-text-secondary">
          {(error as Error).stack}
        </pre>
      )}
    </div>
  );
}

export function defaultHandleError(error: Error, errorInfo: ErrorInfo) {
  // Tag the appointment ID (from the URL) when on an appointment route, so
  // boundary-caught crashes are searchable by appointment in Sentry.
  const appointmentID = detectAppointmentIdFromURL();
  errorReporter.reportReactError(error, errorInfo, {
    // `force` defeats the `_isReported` dedup. Reaching a boundary means the
    // app was replaced by the fallback — a strictly worse outcome than the
    // handled failure some inner code already reported on the same error
    // instance. Without this, every crash whose error was reported on the way
    // up (e.g. AppointmentDetails reporting a fetch failure before rethrowing)
    // is silently dropped, and the crash leaves no Sentry evidence at all:
    // prod had zero `errorType:react_error` events for the auth-expiry crash
    // this flag exists to surface.
    force: true,
    ...(appointmentID ? { tags: { appointmentID } } : {}),
  });
}
